import Phaser from "phaser";
import { CAMERA_RUNTIME } from "../core/CameraRuntime";
import { CodeRainRenderer } from "../view/CodeRainRenderer";
import {
  SYMBOL_SEQUENCE_FONT_KEY,
  SYMBOL_SEQUENCE_FONT_SIZE,
  SYMBOL_SPRITE_FONT_KEY,
} from "../view/SymbolSequenceFont";

const TRANSITION_IN_DURATION_MS = 2000;
const ENDING_TEXT = "Thanks\nfor\nPlaying";
const ENDING_TEXT_SCALE = 2;
const ENDING_TEXT_DEPTH = 10;
const ENDING_TEXT_SCRAMBLE_HOLD_MS = 1000;
const ENDING_TEXT_REVEAL_STEP_MS = 100;
const ENDING_TEXT_SCRAMBLE_INTERVAL_MS = 50;
const ENDING_TEXT_SCRAMBLE_TRACK_LENGTH = 32;
const ENDING_TEXT_SCRAMBLE_GLYPHS = ["◇", "○", "△", "▽", "□", "↺", "③", "⑤"] as const;

interface EndingMessageGlyph {
  readonly actualChar: string;
  readonly revealOrder: number | null;
  readonly text: Phaser.GameObjects.BitmapText;
  readonly scrambleTrack: readonly string[];
  readonly scramblePhaseOffset: number;
}

export class EndingScene extends Phaser.Scene {
  public static readonly KEY = "EndingScene";

  private codeRainRenderer: CodeRainRenderer | null = null;
  private endingMessageGlyphs: EndingMessageGlyph[] = [];
  private endingMessageElapsed: number = 0;
  private endingMessageScrambleElapsed: number = 0;
  private endingMessageStarted: boolean = false;
  private transitionInOverlay: Phaser.GameObjects.Rectangle | null = null;
  private transitionInElapsed: number = 0;
  private transitionInActive: boolean = false;

  public constructor() {
    super(EndingScene.KEY);
  }

  public init(): void {
    this.destroyCodeRain();
    this.destroyEndingMessage();
    this.endingMessageElapsed = 0;
    this.endingMessageScrambleElapsed = 0;
    this.endingMessageStarted = false;
    this.transitionInOverlay = null;
    this.transitionInElapsed = 0;
    this.transitionInActive = false;
  }

  public create(): void {
    this.cameras.main.setBackgroundColor("#000000");

    this.startTransitionIn();

    this.codeRainRenderer = new CodeRainRenderer(
      this,
      CAMERA_RUNTIME.viewportWidthPx,
      CAMERA_RUNTIME.viewportHeightPx,
    );

    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;

    this.createEndingMessage(centerX, centerY);
    this.endingMessageStarted = true;
    this.setEndingMessageVisible(true);
    this.renderEndingMessage();

    this.publishSceneName();
    this.bindCleanup();
  }

  public override update(_time: number, delta: number): void {
    if (delta <= 0) {
      return;
    }

    this.advanceEndingMessageScramble(delta);
    this.advanceTransitionIn(delta);
    if (!this.transitionInActive && this.endingMessageStarted) {
      this.advanceEndingMessage(delta);
    }
    this.codeRainRenderer?.update(delta);
    this.codeRainRenderer?.render();
  }

  private publishSceneName(): void {
    if (!import.meta.env.DEV) {
      return;
    }

    const debugRoot = document.querySelector<HTMLElement>("#debug-root");
    if (debugRoot === null) {
      return;
    }

    let el = debugRoot.querySelector<HTMLElement>("[data-testid=\"scene-name\"]");
    if (el === null) {
      el = document.createElement("div");
      el.dataset["testid"] = "scene-name";
      debugRoot.appendChild(el);
    }

    el.textContent = EndingScene.KEY;
  }

  private bindCleanup(): void {
    this.events.once("shutdown", () => {
      this.destroyTransitionInOverlay();
      this.destroyEndingMessage();
      this.destroyCodeRain();
    });
    this.events.once("destroy", () => {
      this.destroyTransitionInOverlay();
      this.destroyEndingMessage();
      this.destroyCodeRain();
    });
  }

  private createEndingMessage(centerX: number, centerY: number): void {
    const lines = ENDING_TEXT.split("\n");
    const glyphAdvance = SYMBOL_SEQUENCE_FONT_SIZE * ENDING_TEXT_SCALE;
    const totalHeight = lines.length * glyphAdvance;
    const topY = centerY - totalHeight / 2;
    let revealOrder = 0;

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
      const line = lines[lineIndex] ?? "";
      const leftX = centerX - (line.length * glyphAdvance) / 2;
      const y = topY + lineIndex * glyphAdvance;

      for (let charIndex = 0; charIndex < line.length; charIndex += 1) {
        const actualChar = line[charIndex] ?? "";
        const isRevealable = actualChar.trim().length > 0;
        const text = this.add.bitmapText(
          leftX + charIndex * glyphAdvance,
          y,
          isRevealable ? SYMBOL_SPRITE_FONT_KEY : SYMBOL_SEQUENCE_FONT_KEY,
          isRevealable ? ENDING_TEXT_SCRAMBLE_GLYPHS[0] ?? actualChar : actualChar,
          SYMBOL_SEQUENCE_FONT_SIZE,
        );
        text.setOrigin(0, 0);
        text.setScale(ENDING_TEXT_SCALE);
        text.setScrollFactor(0);
        text.setDepth(ENDING_TEXT_DEPTH);
        text.setTint(0xffffff);

        this.endingMessageGlyphs.push({
          actualChar,
          revealOrder: isRevealable ? revealOrder : null,
          text,
          scrambleTrack: isRevealable ? this.createScrambleTrack() : [actualChar],
          scramblePhaseOffset: isRevealable
            ? Math.floor(Math.random() * ENDING_TEXT_SCRAMBLE_TRACK_LENGTH)
            : 0,
        });

        if (isRevealable) {
          revealOrder += 1;
        }
      }
    }
  }

  private createScrambleTrack(): readonly string[] {
    const track: string[] = [];

    for (let index = 0; index < ENDING_TEXT_SCRAMBLE_TRACK_LENGTH; index += 1) {
      const previous = track[track.length - 1];
      let next =
        ENDING_TEXT_SCRAMBLE_GLYPHS[
          Math.floor(Math.random() * ENDING_TEXT_SCRAMBLE_GLYPHS.length)
        ] ?? "";
      if (ENDING_TEXT_SCRAMBLE_GLYPHS.length > 1 && next === previous) {
        const previousIndex = ENDING_TEXT_SCRAMBLE_GLYPHS.indexOf(previous);
        const fallbackIndex =
          previousIndex >= 0 ? (previousIndex + 1) % ENDING_TEXT_SCRAMBLE_GLYPHS.length : 0;
        next = ENDING_TEXT_SCRAMBLE_GLYPHS[fallbackIndex] ?? next;
      }
      track.push(next);
    }

    return track;
  }

  private advanceEndingMessage(delta: number): void {
    this.endingMessageElapsed += Math.max(0, delta);
    this.renderEndingMessage();
  }

  private advanceEndingMessageScramble(delta: number): void {
    this.endingMessageScrambleElapsed += Math.max(0, delta);
    this.renderEndingMessage();
  }

  private renderEndingMessage(): void {
    const revealElapsed = Math.max(0, this.endingMessageElapsed - ENDING_TEXT_SCRAMBLE_HOLD_MS);
    const revealedGlyphCount =
      this.endingMessageElapsed < ENDING_TEXT_SCRAMBLE_HOLD_MS
        ? 0
        : Math.floor(revealElapsed / ENDING_TEXT_REVEAL_STEP_MS);
    const scrambleFrame = Math.floor(
      this.endingMessageScrambleElapsed / ENDING_TEXT_SCRAMBLE_INTERVAL_MS,
    );

    for (const glyph of this.endingMessageGlyphs) {
      if (glyph.revealOrder === null) {
        glyph.text.setFont(SYMBOL_SEQUENCE_FONT_KEY);
        glyph.text.setText(glyph.actualChar);
        continue;
      }

      if (glyph.revealOrder < revealedGlyphCount) {
        glyph.text.setFont(SYMBOL_SEQUENCE_FONT_KEY);
        glyph.text.setText(glyph.actualChar);
        continue;
      }

      glyph.text.setFont(SYMBOL_SPRITE_FONT_KEY);
      glyph.text.setText(
        glyph.scrambleTrack[
          (scrambleFrame + glyph.scramblePhaseOffset) % glyph.scrambleTrack.length
        ] ?? glyph.actualChar,
      );
    }
  }

  private startTransitionIn(): void {
    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000);
    overlay.setAlpha(1);
    overlay.setScrollFactor(0);
    overlay.setDepth(1001);
    this.transitionInOverlay = overlay;
    this.transitionInElapsed = 0;
    this.transitionInActive = true;
  }

  private advanceTransitionIn(delta: number): void {
    if (!this.transitionInActive || this.transitionInOverlay === null) {
      return;
    }

    this.transitionInElapsed += Math.max(0, delta);
    const progress = Math.min(1, this.transitionInElapsed / TRANSITION_IN_DURATION_MS);
    this.transitionInOverlay.setAlpha(1 - progress);

    if (progress < 1) {
      return;
    }

    this.transitionInOverlay.destroy();
    this.transitionInOverlay = null;
    this.transitionInElapsed = 0;
    this.transitionInActive = false;
  }

  private destroyTransitionInOverlay(): void {
    if (this.transitionInOverlay !== null) {
      this.transitionInOverlay.destroy();
      this.transitionInOverlay = null;
    }
    this.transitionInElapsed = 0;
    this.transitionInActive = false;
  }

  private setEndingMessageVisible(visible: boolean): void {
    for (const glyph of this.endingMessageGlyphs) {
      glyph.text.setVisible(visible);
    }
  }

  private destroyEndingMessage(): void {
    for (const glyph of this.endingMessageGlyphs) {
      glyph.text.destroy();
    }
    this.endingMessageGlyphs = [];
    this.endingMessageElapsed = 0;
    this.endingMessageScrambleElapsed = 0;
    this.endingMessageStarted = false;
  }

  private destroyCodeRain(): void {
    this.codeRainRenderer?.destroy();
    this.codeRainRenderer = null;
  }
}
